import type { Item } from './types';

/**
 * Display helpers for the parts of the contract that are honestly nullable.
 *
 * The bot's schema stores ids, not names (`list_members(list_id, user_id,
 * role)`), so every human-readable name on the wire is `string | null`. Rather
 * than making the server invent a placeholder, the fallback lives here — one
 * place, so "User 771002" reads the same everywhere it appears.
 */

export function displayName(name: string | null | undefined, userId: number): string {
  const trimmed = name?.trim();
  return trimmed ? trimmed : `User ${userId}`;
}

/** The owner of a list, from the caller's point of view. */
export function ownerLabel(role: string, ownerName: string | null, ownerId: number): string {
  return role === 'owner' ? 'You' : displayName(ownerName, ownerId);
}

export function initialOf(name: string | null | undefined, fallback = '?'): string {
  const trimmed = name?.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : fallback;
}

/**
 * A *preview* of how the server will split `#tag` out of an item's text.
 *
 * Used for the optimistic add only. The server is authoritative and its
 * response replaces this the moment it lands — so a mismatch costs one frame of
 * a wrongly-placed row, not a wrongly-stored item. That is deliberately unlike
 * the bot's `_resolve()`, where the two inline handlers must parse *identically*
 * or items land in the wrong list; here there is only ever one writer.
 */
export function parseItemPreview(raw: string): { text: string; category: string | null } {
  const match = raw.match(/(^|\s)#([\p{L}\p{N}_-]+)/u);
  if (!match) return { text: raw.trim(), category: null };
  return {
    text: raw.replace(match[0], ' ').replace(/\s+/g, ' ').trim(),
    category: match[2].charAt(0).toUpperCase() + match[2].slice(1).toLowerCase(),
  };
}

/** Item counts for a list, recomputed locally so optimistic edits update them. */
export function countsOf(items: Item[]): { itemCount: number; doneCount: number } {
  return { itemCount: items.length, doneCount: items.filter((i) => i.done).length };
}
